import React, { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { Button, SidebarButton, SidebarHeaderTitle, SidebarTitle, TooltipIcon, TooltipWrapper } from 'components';
import { theme, toRem } from 'design';
import { Size } from 'design/types';
import { SidebarMobileActions } from 'screens/MakePayment/components';
import { ITipCalculatorParticipant } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils';

export interface ISummaryProps {
  dataCy?: string;
  onBack?: () => void;
  onExpand?: () => void;
  sidebarOpen?: boolean;
  totalInstantTips: number;
  paymentDate: Dayjs;
  totalHoldbacks: number;
  totalPayrollTips: number;
  totalTips: number;
  participants?: ITipCalculatorParticipant[];
}

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  padding: ${toRem(16)} ${toRem(40)};

  @media (min-width: ${theme.breakpoint.large}px) {
    flex-grow: 1;
    padding: ${toRem(40)};
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${toRem(24)};

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }
`;

const SummaryContent = styled.div<{ isOpen?: boolean }>`
  /* hide collapsed content in mobile-view */
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-flow: column nowrap;

  @media (min-width: ${theme.breakpoint.large}px) {
    display: flex;
  }
`;

const Column = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(20)};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${toRem(8)};
`;

const Title = styled.p`
  font-size: ${toRem(24)};
  font-weight: ${theme.font.weight.semiBold};
`;

const SubTitle = styled.p`
  font-size: ${toRem(14)};
`;

const PaymentDetails = styled(Column)``;

const LineItem = styled.p`
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.neutral[500]};
`;

const Summary: React.FC<ISummaryProps> = ({ dataCy = 'change-log-summary', onBack, onExpand, totalInstantTips, totalHoldbacks, totalPayrollTips, totalTips, paymentDate, participants, sidebarOpen = false }) => {
  const renderSummaryContent = useMemo(
    () => (
      <SummaryContent isOpen={sidebarOpen}>
        <Column>
          <Row data-cy={`${dataCy}-pay-now`}>
            <Title>Pay Now:</Title>
            <Title>{totalInstantTips > 0 ? centsToDollarsDisplay(totalInstantTips) : '$0.00'}</Title>
          </Row>
          <Row data-cy="change-log-summar-business-date">
            <SubTitle>Business Date:</SubTitle>
            <SubTitle>{paymentDate?.format('MMMM Do, YYYY')}</SubTitle>
          </Row>
        </Column>
        <PaymentDetails>
          <Row data-cy={`${dataCy}-payment-details`}>
            <SubTitle>Payment Details</SubTitle>
          </Row>
          <Row data-cy={`${dataCy}-amount-entered`}>
            <LineItem>Amount Entered (Gross)</LineItem>
            <LineItem>{totalTips > 0 ? centsToDollarsDisplay(totalTips) : '$0.00'}</LineItem>
          </Row>
          <Row data-cy={`${dataCy}-holdbacks`}>
            <TooltipWrapper>
              <LineItem>Holdbacks</LineItem>
              <TooltipIcon id="holdback">Amount withheld to cover expenses</TooltipIcon>
            </TooltipWrapper>
            <LineItem>{totalHoldbacks > 0 ? centsToNegativeDollarsDisplay(totalHoldbacks) : '$0.00'}</LineItem>
          </Row>
          <Row data-cy={`${dataCy}-instant`}>
            <LineItem>Instant</LineItem>

            <LineItem>{totalInstantTips > 0 ? centsToDollarsDisplay(totalInstantTips) : '$0.00'}</LineItem>
          </Row>
          <Row data-cy={`${dataCy}-payroll`}>
            <LineItem>Payroll</LineItem>
            <LineItem>{totalPayrollTips > 0 ? centsToDollarsDisplay(totalPayrollTips) : '$0.00'}</LineItem>
          </Row>
          {participants?.map((participant) => (
            <Row key={`participant_${participant.name}`} data-cy={`${dataCy}-participant`}>
              <LineItem>{participant.name}</LineItem>
              <LineItem>{centsToDollarsDisplay(participant.net)}</LineItem>
            </Row>
          ))}
        </PaymentDetails>
      </SummaryContent>
    ),
    [sidebarOpen]
  );

  return (
    <Layout data-cy={dataCy}>
      <MobileHeader>
        <SidebarHeaderTitle>
          <SidebarButton onClick={onExpand} isOpen={sidebarOpen} />
          <SidebarTitle title="Change Log Details" />
        </SidebarHeaderTitle>
        <SidebarMobileActions columns={2}>
          <Button data-cy={`${dataCy}-back-button`} label="Back" type="button" appearance="white" size="small" onClick={onBack} />
        </SidebarMobileActions>
      </MobileHeader>
      {renderSummaryContent}
      <Button data-cy={`${dataCy}-back-button`} label="Back" type="button" appearance="white" size={Size.LARGE} onClick={onBack} />
    </Layout>
  );
};

export default Summary;
